// ============================================================
// ASSET BOOKING — equipment booked beside the courts.
//
// A ball machine, a rental racket set, a camera: one piece of equipment, kept
// at one centre, reserved for a time range on a day — on its own, or riding
// with a court booking. See docs/domains/ASSET_BOOKING.md.
//
// WHAT IS SHARED WITH THE COURT ENGINE, AND WHAT DELIBERATELY IS NOT.
//
// Shared, by IMPORT rather than by copy: the time helpers, and `calcBookingPrice`
// — an asset carries price zones in the booking engine's own zone shape, so one
// pricing function serves courts and equipment and a zone edited for a ball
// machine behaves exactly like a zone edited for the courts. The calendar
// rules an asset keeps are the court calendar's: the half-hour grid, the
// centre's opening hours (when the caller asks for them), and a direct overlap
// is always blocked.
//
// NOT shared: `validateBooking` itself. It is court-shaped through and through
// — `candidate.court` as identity, smart allocation across the court list, and
// Norwegian refusal sentences the booking screens parse — and bending it to take
// «a resource» would change the function the court calendar runs on, which is
// exactly what CLAUDE.md forbids happening as a side effect. So this is a
// SMALLER validator for a simpler thing: there is no smart allocation (two ball
// machines are not a calendar to pack), the gap rule is per asset and off by
// default, and refusals are CODES — the server returns them untranslated and
// the browser picks the sentence (CLAUDE.md § i18n).
//
// No storage, no React. Pinned by packages/engine/tests/asset-booking.test.ts.
// ============================================================

import { toMinHHMM, fromMinHHMM } from './time.js';
import { calcBookingPrice } from './booking.js';

/** The shape of an asset the engine reads — a row, or the client's copy of one. */
export interface AssetLike {
  id: string;
  name?: string;
  /** Whether it is OFFERED. Inactive equipment keeps its bookings and takes no new ones. */
  active?: boolean;
  /** The smallest gap allowed between two bookings of this asset. 0/null = any. */
  bufferMin?: number | null;
  /** Price zones in the booking engine's zone shape. Null/empty = default price only. */
  priceZones?: unknown;
  /** Kroner per hour where no zone applies. */
  defaultPricePerHour?: number | null;
}

/** One reservation of one asset, as the engine reads it. */
export interface AssetBookingLike {
  id?: string;
  assetId: string;
  date: string;
  startTime: string;
  endTime: string;
}

/** A court booking, as much of it as the attachment rule needs. */
export interface BookingWindowLike {
  date: string;
  startTime: string;
  endTime?: string | null;
}

/**
 * Why an asset booking is refused. CODES, never sentences: the API answers
 * with the code, and the browser owns the words (`errors.asset.*` in the
 * catalog). `outside_opening_hours` is spelled exactly as the court validator
 * spells it, so a screen that already knows that refusal knows this one.
 */
export type AssetBookingRefusalCode =
  | 'missing_fields'
  | 'end_before_start'
  | 'off_grid'
  | 'outside_opening_hours'
  | 'asset_taken'
  | 'dead_gap';

export interface AssetBookingValidation {
  ok: boolean;
  code?: AssetBookingRefusalCode;
  /** The booking in the way, for `asset_taken` and `dead_gap`. */
  conflictId?: string;
  /** What the code is about: the window for the hours, the gap and its limit. */
  detail?: Record<string, string | number>;
}

export interface AssetBookingRules {
  /**
   * The smallest gap allowed between two bookings of the asset. A gap must be
   * 0 (back-to-back) or at least this many minutes; 0/null disables the rule,
   * which is the default — a ball machine has no «dead gap» problem.
   */
  bufferMin?: number | null;
  /**
   * The centre's opening hours. BOTH or neither, exactly as `validateBooking`
   * reads them: absent means unchecked, which is how the admin override asks
   * for the rule to be skipped.
   */
  openTime?: string | null;
  closeTime?: string | null;
}

/**
 * A time range in minutes since midnight, with an end on or before the start
 * read as the NEXT day — the court engine's over-midnight rule (23:30–00:30 is
 * an hour, not minus twenty-three of them).
 */
export function assetTimeRange(
  startTime: string,
  endTime: string,
): { startMin: number; endMin: number } {
  const startMin = toMinHHMM(startTime);
  let endMin = toMinHHMM(endTime);
  if (endMin <= startMin) endMin += 1440;
  return { startMin, endMin };
}

/** `HH:MM` + minutes → `HH:MM`, wrapping past midnight like the court engine. */
export function assetBookingEndTime(startTime: string, durationMin: number): string {
  return fromMinHHMM(toMinHHMM(startTime) + durationMin);
}

/**
 * The normalised candidate the validator and the writer agree on: `endTime`
 * from the duration when only the duration is given, and the duration from
 * the times when only the end is.
 */
export function assetBookingCandidate(input: {
  assetId: string;
  date: string;
  startTime: string;
  durationMin?: number | null;
  endTime?: string | null;
  id?: string;
}): AssetBookingLike & { durationMin: number } {
  const startTime = String(input.startTime ?? '');
  let endTime = input.endTime ? String(input.endTime) : '';
  let durationMin = typeof input.durationMin === 'number' ? input.durationMin : 0;
  if (!endTime && durationMin > 0) endTime = assetBookingEndTime(startTime, durationMin);
  if (endTime && durationMin <= 0) {
    const { startMin, endMin } = assetTimeRange(startTime, endTime);
    durationMin = endMin - startMin;
  }
  return {
    ...(input.id ? { id: input.id } : {}),
    assetId: String(input.assetId ?? ''),
    date: String(input.date ?? ''),
    startTime,
    endTime,
    durationMin,
  };
}

/**
 * May this asset be booked for this range?
 *
 * The rules, in the order they are checked — the first refusal wins:
 *   1. the four fields are present;
 *   2. the end is after the start (over midnight counts as after);
 *   3. start and end sit on the half hour, as every booking in the calendar does;
 *   4. inside the centre's opening hours, WHEN the caller passes them;
 *   5. no direct overlap with another booking of the SAME asset on the same
 *      day — always, whatever else is relaxed;
 *   6. the asset's own gap rule, when it has one.
 *
 * `existing` may hold other assets' bookings and other days; only the same
 * asset on the same date is read, and the candidate's own id is skipped so a
 * re-timing validates against everything but itself.
 */
export function validateAssetBooking(
  candidate: Partial<AssetBookingLike>,
  existing: readonly AssetBookingLike[] = [],
  rules: AssetBookingRules = {},
): AssetBookingValidation {
  if (!candidate?.assetId || !candidate.date || !candidate.startTime || !candidate.endTime) {
    return { ok: false, code: 'missing_fields' };
  }
  const startMin = toMinHHMM(candidate.startTime);
  let endMin = toMinHHMM(candidate.endTime);
  if (endMin < startMin) endMin += 1440;
  if (endMin === startMin) return { ok: false, code: 'end_before_start' };
  if (startMin % 30 !== 0 || endMin % 30 !== 0) return { ok: false, code: 'off_grid' };

  // The window is read exactly as `validateBooking` and `findFreeSlots` read it:
  // a closing time on or before opening means the centre closes the NEXT day,
  // and a candidate before opening on such a window is the LATE part of the day.
  if (rules.openTime != null && rules.closeTime != null) {
    const openMin = toMinHHMM(rules.openTime);
    let closeMin = toMinHHMM(rules.closeTime);
    if (closeMin <= openMin) closeMin += 1440;
    let s = startMin;
    let e = endMin;
    if (closeMin > 1440 && s < openMin) {
      s += 1440;
      e += 1440;
    }
    if (s < openMin || e > closeMin) {
      return {
        ok: false,
        code: 'outside_opening_hours',
        detail: { openTime: rules.openTime, closeTime: rules.closeTime },
      };
    }
  }

  const sameAssetDay = existing.filter(
    (b) =>
      b.assetId === candidate.assetId &&
      b.date === candidate.date &&
      (candidate.id == null || b.id !== candidate.id),
  );
  // Existing ranges get the same over-midnight reading as the candidate, so a
  // machine booked 23:30–00:30 is busy at 00:00 rather than free by arithmetic.
  const ranges = sameAssetDay.map((b) => ({ b, ...assetTimeRange(b.startTime, b.endTime) }));

  for (const r of ranges) {
    if (startMin < r.endMin && endMin > r.startMin) {
      return { ok: false, code: 'asset_taken', conflictId: r.b.id };
    }
  }

  const bufferMin = rules.bufferMin ?? 0;
  if (bufferMin > 0) {
    // Only the nearest neighbour on each side matters, as for courts: anything
    // further away has another booking between it and the candidate.
    let prev: (typeof ranges)[number] | null = null;
    let next: (typeof ranges)[number] | null = null;
    for (const r of ranges) {
      if (r.endMin <= startMin && (!prev || r.endMin > prev.endMin)) prev = r;
      if (r.startMin >= endMin && (!next || r.startMin < next.startMin)) next = r;
    }
    if (prev) {
      const gap = startMin - prev.endMin;
      if (gap > 0 && gap < bufferMin) {
        return {
          ok: false,
          code: 'dead_gap',
          conflictId: prev.b.id,
          detail: { gapMin: gap, bufferMin },
        };
      }
    }
    if (next) {
      const gap = next.startMin - endMin;
      if (gap > 0 && gap < bufferMin) {
        return {
          ok: false,
          code: 'dead_gap',
          conflictId: next.b.id,
          detail: { gapMin: gap, bufferMin },
        };
      }
    }
  }
  return { ok: true };
}

/**
 * The price of booking `asset` for the range, through the ONE pricing function.
 *
 * The asset's zones replace the centre's zones and its default price replaces
 * the centre's default; everything else in `cfg` — the holiday country and the
 * club's added or removed holidays — is the centre's, because a holiday is a
 * property of the day, not of the equipment. Same return shape as
 * `calcBookingPrice`: `{ total, breakdown }`.
 */
export function calcAssetBookingPrice(
  asset: AssetLike,
  isoDate: string,
  startTime: string,
  endTime: string,
  cfg: Record<string, unknown> | null = null,
): { total: number; breakdown: unknown[] } {
  const zones = Array.isArray(asset.priceZones) ? asset.priceZones : [];
  const priceCfg = { ...(cfg ?? {}), defaultPricePerHour: asset.defaultPricePerHour ?? 0 };
  return calcBookingPrice(zones, isoDate, startTime, endTime, priceCfg) as {
    total: number;
    breakdown: unknown[];
  };
}

/**
 * Does the asset booking sit INSIDE the court booking it would ride with?
 *
 * Same day, and its range within the court's. A machine for the first hour of
 * a two-hour court is fine; a machine that outlasts the court is not riding
 * with anything, and a machine on another day is a different reservation.
 */
export function assetBookingWithinBooking(
  ab: { date: string; startTime: string; endTime: string },
  booking: BookingWindowLike,
): boolean {
  if (!booking?.date || !booking.startTime || !booking.endTime) return false;
  if (ab.date !== booking.date) return false;
  const a = assetTimeRange(ab.startTime, ab.endTime);
  const b = assetTimeRange(booking.startTime, booking.endTime);
  return a.startMin >= b.startMin && a.endMin <= b.endMin;
}

/**
 * Where an attached asset booking lands when its court booking MOVES.
 *
 * It keeps its offset from the court's start and its length, and is clipped to
 * the court's new end: a machine for the first hour of an 18:00–20:00 court
 * becomes the first hour of the court wherever that court goes, and a court
 * that shrinks takes the machine down with it. A `durationMin` of zero or less
 * means the equipment no longer fits inside the booking at all, and the caller
 * refuses the move rather than keeping a reservation for nothing.
 */
export function moveAssetBookingWithBooking(
  ab: { startTime: string; endTime: string },
  from: { startTime: string; endTime: string },
  to: { date: string; startTime: string; endTime: string },
): { date: string; startTime: string; endTime: string; durationMin: number } {
  const oldCourt = assetTimeRange(from.startTime, from.endTime);
  const newCourt = assetTimeRange(to.startTime, to.endTime);
  const oldAsset = assetTimeRange(ab.startTime, ab.endTime);
  const offset = Math.max(0, oldAsset.startMin - oldCourt.startMin);
  const startMin = newCourt.startMin + offset;
  const endMin = Math.min(startMin + (oldAsset.endMin - oldAsset.startMin), newCourt.endMin);
  return {
    date: to.date,
    startTime: fromMinHHMM(startMin),
    endTime: fromMinHHMM(endMin),
    durationMin: endMin - startMin,
  };
}

/**
 * The assets that could be booked for this range — active, and free of
 * conflicts under each asset's own gap rule. What the «legg til utstyr» picker
 * offers beside a court booking, and what the member screen will offer later.
 */
export function availableAssetsFor(
  assets: readonly AssetLike[],
  assetBookings: readonly AssetBookingLike[],
  range: { date: string; startTime: string; endTime: string; excludeId?: string | null },
  hours: Pick<AssetBookingRules, 'openTime' | 'closeTime'> = {},
): AssetLike[] {
  return assets.filter((asset) => {
    if (asset.active === false) return false;
    const v = validateAssetBooking(
      {
        ...(range.excludeId ? { id: range.excludeId } : {}),
        assetId: asset.id,
        date: range.date,
        startTime: range.startTime,
        endTime: range.endTime,
      },
      assetBookings,
      { bufferMin: asset.bufferMin ?? 0, openTime: hours.openTime, closeTime: hours.closeTime },
    );
    return v.ok;
  });
}

/** One start time with the assets free for `durationMin` from it. */
export interface AssetSlot {
  startTime: string;
  endTime: string;
  freeAssetIds: string[];
}

/**
 * Every start time on the day's grid with at least one asset free for the
 * duration — the equipment counterpart of `findFreeSlots`, reading the window
 * the same way (closing on or before opening = next day; 00:00–00:00 = all day).
 */
export function findFreeAssetSlots(
  assets: readonly AssetLike[],
  assetBookings: readonly AssetBookingLike[],
  isoDate: string,
  durationMin: number,
  {
    openTime = '07:00',
    closeTime = '23:00',
    stepMin = 30,
  }: { openTime?: string; closeTime?: string; stepMin?: number } = {},
): AssetSlot[] {
  const openMin = toMinHHMM(openTime);
  let closeMin = toMinHHMM(closeTime);
  if (closeMin <= openMin) closeMin += 1440;
  const dayBookings = assetBookings.filter((b) => b.date === isoDate);
  const slots: AssetSlot[] = [];
  for (let t = openMin; t + durationMin <= closeMin; t += stepMin) {
    const startTime = fromMinHHMM(t);
    const endTime = fromMinHHMM(t + durationMin);
    const free = availableAssetsFor(
      assets,
      dayBookings,
      { date: isoDate, startTime, endTime },
      { openTime, closeTime },
    );
    if (free.length > 0) slots.push({ startTime, endTime, freeAssetIds: free.map((a) => a.id) });
  }
  return slots;
}

/** The asset bookings on one day — optionally of one asset — in start order. */
export function assetBookingsForDay<T extends AssetBookingLike>(
  assetBookings: readonly T[],
  isoDate: string,
  assetId: string | null = null,
): T[] {
  return assetBookings
    .filter((b) => b.date === isoDate && (assetId == null || b.assetId === assetId))
    .sort((a, b) => toMinHHMM(a.startTime) - toMinHHMM(b.startTime));
}
